import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import Wrapper from '@/components/atoms/legacy/Wrapper';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import FeaturedBlog from '@/components/molecules/FeaturedBlog/FeaturedBlog';
import { FollowBlog } from '@/components/molecules/FollowBlog';
import {
  BlogListingLink,
  BlogListingLinkSkeleton,
} from '@/components/organisms/BlogListingLink';
import { useInfiniteArticles } from '@/hooks/queries/articles';
import { BlogPreview } from '@/types/blog';
import React, { useCallback, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { LatestNewsSection } from './LatestNewsSection';

export interface BlogLandingTemplateProps {
  posts: BlogPreview[];
  categories: { id: string; name: string }[];
}

const BlogLandingTemplate: React.FC<BlogLandingTemplateProps> = ({
  posts,
  categories,
}) => {
  const pageSize = 8;

  const [selectedCategory, setSelectedCategory] = useState(
    categories.length ? categories[0].id : undefined
  );

  const blogPostsIdsToExclude = useMemo(() => posts.map((p) => p.id), [posts]);

  const [featuredArticle, ...latestNewsArticles] = posts;

  const { ref: articleGridRef, inView: otherArticlesInView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteArticles({
      queryKey: ['blogPostsByCategory', selectedCategory],
      enabled: otherArticlesInView,
      initialPageParam: 1,
      perPage: pageSize,
      exclude: blogPostsIdsToExclude,
      categories: selectedCategory,
    });

  // We only show skeletons if we're fetching the 1st page of data
  const showLoadingSkeletons = !data && isFetching && !isFetchingNextPage;

  const handleCategoryChange = useCallback((newsCategoryId: string) => {
    setSelectedCategory(newsCategoryId);
  }, []);

  return (
    <>
      <div className="mx-auto max-w-[1920px] bg-gradient-to-b from-lightTheme-start to-lightTheme-end">
        <FeaturedBlog blog={featuredArticle} />
      </div>
      <div className="mx-auto max-w-[1920px] bg-neutral-100">
        <LatestNewsSection posts={latestNewsArticles} />
      </div>
      <div
        className="mx-auto max-w-[1920px] bg-neutral-100"
        ref={articleGridRef}
      >
        <section className="flex flex-col gap-xl px-l py-xl pt-[40px] lg:px-[64px] lg:py-[40px]">
          <h3 className="text-h3SemiBold">All articles</h3>
          <section className="flex w-full max-w-screen-lg flex-row gap-s overflow-x-auto">
            {categories.map(({ name: topic, id }) => (
              <Badge
                key={topic}
                label={topic}
                className="mb-[8px] mr-[8px] cursor-pointer text-[12px] md:text-[16px]"
                onClick={() => handleCategoryChange(id)}
                variant={
                  selectedCategory === id
                    ? BadgeVariants.Blue
                    : BadgeVariants.Gray
                }
              />
            ))}
          </section>
          <section className="flex flex-col md:flex-row">
            <div className="grid w-full grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2 lg:grid-cols-4">
              {showLoadingSkeletons
                ? Array.from({ length: pageSize }).map((_, index) => (
                    <div key={index} className="h-full">
                      <BlogListingLinkSkeleton layout="column" />
                    </div>
                  ))
                : null}
              {data?.pages.map(({ blogPosts }) =>
                blogPosts.map((blog) => {
                  return (
                    <div key={blog.id} className="h-full">
                      <BlogListingLink blog={blog} layout="column" />
                    </div>
                  );
                })
              )}
            </div>
          </section>
          {hasNextPage && (
            <div className="w-full self-center md:max-w-[262px]">
              <Button
                variant={ButtonVariants.Secondary}
                onClick={() => fetchNextPage()}
                disabled={!hasNextPage || isFetchingNextPage}
                loading={!hasNextPage || isFetchingNextPage}
                aria-label="Show more articles"
              >
                Show more
              </Button>
            </div>
          )}
        </section>
      </div>
      <Wrapper className="bg-gradient-to-b from-lightTheme-start to-lightTheme-end px-xl pb-[60px] pt-xl md:p-[64px]">
        <FollowBlog />
      </Wrapper>
    </>
  );
};

export default BlogLandingTemplate;
